/**
 * What a person locked out actually reads.
 *
 * `docs/SOCIAL_LOGIN.md` § Copy: these errors are read by someone who cannot get in, so they
 * say what happened and what to do. No apologies, no "oops", no raw error code standing alone.
 * The four sentences the document writes out verbatim are used verbatim — with the tenant's
 * product name in place of "FTK Connector", because white label replaces that word everywhere.
 *
 * Each branch below is exactly one catalogue key with named placeholders, never a sentence
 * assembled from fragments: `docs/I18N.md` forbids the second, and a locked-out person is the
 * last reader who should meet a half-translated sentence. Where a branch has a version that
 * names the provider and one that does not, they are *two keys*, because Italian does not put
 * the name where English does.
 *
 * The provider's own name — Google, Facebook — is never translated. It arrives as a placeholder.
 */

import type { TFunction } from '../../../lib/i18n'
import { SOCIAL_LABEL } from './contract'
import type { SocialProvider } from './contract'

export interface SocialCopyContext {
  /** `null` when the callback did not say which provider it came back from. */
  provider: SocialProvider | null
  /** The tenant's product name — "FTK Connector" unless a partner replaced it. */
  productName: string
  supportEmail: string | null
}

export interface SocialErrorCopy {
  title: string
  body: string
  /** Whether offering the provider button again can plausibly help. */
  retryable: boolean
}

/** `Google` when we know, otherwise the translated stand-in — never a bare English word. */
const nameOf = (t: TFunction, provider: SocialProvider | null): string =>
  provider ? SOCIAL_LABEL[provider] : t('auth.social.providerFallback')

/** Copy for an outcome the callback reported, keyed by the normalised code. */
export const socialErrorCopy = (
  t: TFunction,
  code: string,
  ctx: SocialCopyContext,
): SocialErrorCopy => {
  const { provider, productName, supportEmail } = ctx
  const label = provider ? SOCIAL_LABEL[provider] : null

  switch (code) {
    case 'OAUTH_DENIED':
      return {
        title: label
          ? t('auth.social.deniedTitle', { provider: label })
          : t('auth.social.deniedTitleGeneric'),
        body: label
          ? t('auth.social.deniedBody', { provider: label })
          : t('auth.social.deniedBodyGeneric'),
        retryable: true,
      }

    case 'OAUTH_STATE_INVALID':
      return {
        title: t('auth.social.stateInvalidTitle'),
        body: t('auth.social.stateInvalidBody'),
        retryable: true,
      }

    case 'OAUTH_EXCHANGE_FAILED':
      return {
        title: label
          ? t('auth.social.exchangeTitle', { provider: label })
          : t('auth.social.failedTitle'),
        body: t('auth.social.exchangeBody', { provider: nameOf(t, provider) }),
        retryable: true,
      }

    case 'OAUTH_NOT_CONFIGURED':
      return {
        title: label
          ? t('auth.social.notConfiguredTitle', { provider: label })
          : t('auth.social.notConfiguredTitleGeneric'),
        body: label
          ? t('auth.social.notConfiguredBody', { provider: label })
          : t('auth.social.notConfiguredBodyGeneric'),
        retryable: false,
      }

    case 'OAUTH_DISABLED':
      return {
        title: label
          ? t('auth.social.disabledTitle', { provider: label })
          : t('auth.social.disabledTitleGeneric'),
        body: t('auth.social.disabledBody', { productName }),
        retryable: false,
      }

    case 'ACCOUNT_SUSPENDED':
      return {
        title: t('auth.social.suspendedTitle'),
        body: supportEmail
          ? t('auth.social.suspendedBody', { email: supportEmail })
          : t('auth.social.suspendedBodyNoSupport'),
        retryable: false,
      }

    case 'RATE_LIMITED':
      return {
        title: t('auth.social.rateLimitedTitle'),
        body: t('auth.social.rateLimitedBody'),
        retryable: true,
      }

    default:
      return {
        title: t('auth.social.failedTitle'),
        body: t('auth.social.failedBody'),
        retryable: true,
      }
  }
}

/* ── the three named screens ──────────────────────────────────────────────── */

/** `docs/SOCIAL_LOGIN.md` § Copy, first line. The screen is at `EmailInUseScreen`. */
export const emailInUseCopy = (
  t: TFunction,
  ctx: SocialCopyContext,
): { title: string; body: string; afterwards: string } => {
  const label = ctx.provider
    ? SOCIAL_LABEL[ctx.provider]
    : t('auth.social.providerFallbackLower')
  return {
    title: t('auth.social.emailInUseTitle'),
    body: t('auth.social.emailInUseBody', { productName: ctx.productName, provider: label }),
    afterwards: t('auth.social.emailInUseAfter', { provider: label }),
  }
}

/** `docs/SOCIAL_LOGIN.md` § Copy, second line. The screen is at `MissingEmailScreen`. */
export const missingEmailCopy = (
  t: TFunction,
  ctx: SocialCopyContext,
): { title: string; body: string; hint: string } => {
  const label = ctx.provider ? SOCIAL_LABEL[ctx.provider] : t('auth.social.providerFallback')
  return {
    title: t('auth.social.missingEmailTitle', { provider: label }),
    body: t('auth.social.missingEmailBody', { provider: label }),
    hint: t('auth.social.missingEmailHint'),
  }
}

/**
 * `docs/SOCIAL_LOGIN.md` § Copy, third line — shown when the password form is used on an
 * account created through a provider. `User.passwordHash` is null there, and inventing one
 * would be a lie, so the copy names the provider the account actually uses.
 */
export const passwordlessAccountCopy = (
  t: TFunction,
  provider: SocialProvider | null,
): string =>
  provider
    ? t('auth.social.passwordlessBody', { provider: SOCIAL_LABEL[provider] })
    : t('auth.social.passwordlessBodyGeneric')

/**
 * `docs/SOCIAL_LOGIN.md` § Copy, fourth line — the unlink refusal. Lives here rather than with
 * the settings panel so all four sentences the document writes out stay in one file.
 */
export const unlinkLockoutCopy = (t: TFunction, provider: SocialProvider): string =>
  t('auth.social.unlinkLockout', { provider: SOCIAL_LABEL[provider] })

/** The unlink refusal when another identity — not a password — is the thing to arrange first. */
export const unlinkLastMethodCopy = (t: TFunction, provider: SocialProvider): string =>
  t('auth.social.unlinkLastMethod', { provider: SOCIAL_LABEL[provider] })
